import {
  PaymentMethodLabelInput,
  resolvePaymentMethodLabel,
} from '../../../utils/paymentMethodLabels';

export const FINANCIAL_ENTRY_TYPE = {
  REVENUE: 0,
  EXPENSE: 1,
} as const;

export const FINANCIAL_ENTRY_STATUS = {
  PROVISIONED: 0,
  CONTRACTED: 1,
  INVOICED: 2,
  PAID: 3,
} as const;

export const FINANCIAL_ENTRY_CATEGORY = {
  SERVICE: 0,
  INTEREST: 1,
  ASSETS: 2,
  JUDICIAL: 3,
  RENT: 4,
  SALARY: 5,
  STOCK: 6,
  OCCUPATION: 7,
} as const;

export const FINANCIAL_ENTRY_SUBCATEGORY = {
  RAW_MATERIAL_RESALE: 1,
  FREIGHT_ON_SALES: 2,
  RAW_MATERIAL_DOMESTIC: 3,
  MACHINING_LABOR_SHIPMENT: 4,
  PRODUCTION_INPUTS: 5,
  RAW_MATERIAL_FOREIGN: 6,
  HEAT_TREATMENT_SHIPMENT: 7,
  INDUSTRIALIZATION_HEAT_TREATMENT: 8,
  INDUSTRIALIZATION_FORGING: 9,
  INDUSTRIALIZATION_MACHINING: 10,
  INDUSTRIALIZATION_CUTTING: 11,
  INDUSTRIALIZATION_OTHER_SERVICES: 12,
  INDUSTRIALIZATION_TESTS: 13,
  INDUSTRIALIZATION_GLP: 14,
} as const;

export const COMPETENCE_MONTH_OPTIONS = [
  { value: '01', label: 'Janeiro' },
  { value: '02', label: 'Fevereiro' },
  { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Maio' },
  { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
] as const;

export const FINANCIAL_ENTRY_RECURRENCE = {
  MONTHLY: 0,
  QUARTERLY: 1,
  SEMIANNUAL: 2,
  YEARLY: 3,
} as const;

export const FINANCIAL_ENTRY_SORT_BY = {
  DUE_DATE: 'dueDate',
  CREATED_AT: 'createdAt',
  TOTAL_VALUE: 'totalValue',
} as const;

export type FinancialEntrySortByValue =
  (typeof FINANCIAL_ENTRY_SORT_BY)[keyof typeof FINANCIAL_ENTRY_SORT_BY];

export const FINANCIAL_VIEW_BY = {
  ENTRY: 'entry',
  PAYMENT: 'payment',
  ITEM: 'item',
} as const;

export type FinancialViewByValue =
  (typeof FINANCIAL_VIEW_BY)[keyof typeof FINANCIAL_VIEW_BY];

export const FINANCIAL_VIEW_BY_OPTIONS = [
  { value: FINANCIAL_VIEW_BY.ENTRY, label: 'Lançamentos' },
  { value: FINANCIAL_VIEW_BY.PAYMENT, label: 'Pagamentos' },
  { value: FINANCIAL_VIEW_BY.ITEM, label: 'Itens' },
];

export const FINANCIAL_ENTRY_TYPE_OPTIONS = [
  { value: FINANCIAL_ENTRY_TYPE.REVENUE, label: 'Entrada' },
  { value: FINANCIAL_ENTRY_TYPE.EXPENSE, label: 'Saída' },
];

export const FINANCIAL_ENTRY_TYPE_FILTER_OPTIONS = [
  { value: FINANCIAL_ENTRY_TYPE.REVENUE, label: 'A receber' },
  { value: FINANCIAL_ENTRY_TYPE.EXPENSE, label: 'A pagar' },
];

export const FINANCIAL_ENTRY_TYPE_TABLE_LABELS: Record<number, string> = {
  [FINANCIAL_ENTRY_TYPE.REVENUE]: '(+) Receber',
  [FINANCIAL_ENTRY_TYPE.EXPENSE]: '(-) Pagar',
};

export const FINANCIAL_ENTRY_STATUS_OPTIONS = [
  { value: FINANCIAL_ENTRY_STATUS.PROVISIONED, label: 'Provisionado' },
  { value: FINANCIAL_ENTRY_STATUS.CONTRACTED, label: 'Contratado' },
  { value: FINANCIAL_ENTRY_STATUS.INVOICED, label: 'Faturado' },
  { value: FINANCIAL_ENTRY_STATUS.PAID, label: 'Pago' },
];

export const FINANCIAL_ENTRY_CATEGORY_OPTIONS = [
  { value: FINANCIAL_ENTRY_CATEGORY.SERVICE, label: 'Serviço' },
  { value: FINANCIAL_ENTRY_CATEGORY.INTEREST, label: 'Juros' },
  { value: FINANCIAL_ENTRY_CATEGORY.ASSETS, label: 'Ativos' },
  { value: FINANCIAL_ENTRY_CATEGORY.JUDICIAL, label: 'Judicial' },
  { value: FINANCIAL_ENTRY_CATEGORY.RENT, label: 'Aluguel' },
  { value: FINANCIAL_ENTRY_CATEGORY.SALARY, label: 'Salário' },
  { value: FINANCIAL_ENTRY_CATEGORY.STOCK, label: 'Estoque' },
  { value: FINANCIAL_ENTRY_CATEGORY.OCCUPATION, label: 'Ocupação' },
];

export const FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS = [
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.RAW_MATERIAL_RESALE,
    label: 'Compras de Matéria - Prima para Revenda',
  },
  { value: FINANCIAL_ENTRY_SUBCATEGORY.FREIGHT_ON_SALES, label: 'Frete Sobre Vendas' },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.RAW_MATERIAL_DOMESTIC,
    label: 'Compras de Matéria Prima Mercado Interno',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.MACHINING_LABOR_SHIPMENT,
    label: 'Remessa para Mão de Obra de Usinagem',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.PRODUCTION_INPUTS,
    label: 'Compra de Insumos para Produção',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.RAW_MATERIAL_FOREIGN,
    label: 'Compra de Matéria Prima Mercado Externo',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.HEAT_TREATMENT_SHIPMENT,
    label: 'Remessa para Tratamento Térmico',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_HEAT_TREATMENT,
    label: 'Custo de Industrialização / Tratamento Térmico',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_FORGING,
    label: 'Custo de Industrialização / Forjamento',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_MACHINING,
    label: 'Custo de Industrialização / Usinagem',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_CUTTING,
    label: 'Custo de Industrialização / Corte',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_OTHER_SERVICES,
    label: 'Custo de Industrialização Outros Serviços',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_TESTS,
    label: 'Custo de Industrialização / Ensaios',
  },
  {
    value: FINANCIAL_ENTRY_SUBCATEGORY.INDUSTRIALIZATION_GLP,
    label: 'Custo de Industrialização / GLP',
  },
];

export const FINANCIAL_ENTRY_RECURRENCE_OPTIONS = [
  { value: FINANCIAL_ENTRY_RECURRENCE.MONTHLY, label: 'Mensal' },
  { value: FINANCIAL_ENTRY_RECURRENCE.QUARTERLY, label: 'Trimestral' },
  { value: FINANCIAL_ENTRY_RECURRENCE.SEMIANNUAL, label: 'Semestral' },
  { value: FINANCIAL_ENTRY_RECURRENCE.YEARLY, label: 'Anual' },
];

export const FINANCIAL_ENTRY_SORT_BY_ENTRY_OPTIONS = [
  { value: FINANCIAL_ENTRY_SORT_BY.CREATED_AT, label: 'Registro' },
  { value: FINANCIAL_ENTRY_SORT_BY.DUE_DATE, label: 'Vencimento' },
  { value: FINANCIAL_ENTRY_SORT_BY.TOTAL_VALUE, label: 'Valor total' },
] as const;

export const FINANCIAL_ENTRY_SORT_BY_PAYMENT_OPTIONS = [
  { value: FINANCIAL_ENTRY_SORT_BY.CREATED_AT, label: 'Registro' },
  { value: FINANCIAL_ENTRY_SORT_BY.DUE_DATE, label: 'Vencimento' },
  { value: FINANCIAL_ENTRY_SORT_BY.TOTAL_VALUE, label: 'Valor' },
] as const;

export const FINANCIAL_ENTRY_SORT_BY_ITEM_OPTIONS = [
  { value: FINANCIAL_ENTRY_SORT_BY.CREATED_AT, label: 'Registro' },
  { value: FINANCIAL_ENTRY_SORT_BY.DUE_DATE, label: 'Vencimento' },
  { value: FINANCIAL_ENTRY_SORT_BY.TOTAL_VALUE, label: 'Valor total' },
] as const;

export const FINANCIAL_ENTRY_SORT_BY_OPTIONS = FINANCIAL_ENTRY_SORT_BY_ENTRY_OPTIONS;

export const STATUS_BADGE_COLORS: Record<number, { bg: string; color: string }> = {
  [FINANCIAL_ENTRY_STATUS.PROVISIONED]: { bg: '#F3F4F6', color: '#374151' },
  [FINANCIAL_ENTRY_STATUS.CONTRACTED]: { bg: '#FCE7F3', color: '#BE185D' },
  [FINANCIAL_ENTRY_STATUS.INVOICED]: { bg: '#E0E7FF', color: '#1D4ED8' },
  [FINANCIAL_ENTRY_STATUS.PAID]: { bg: '#D1FAE5', color: '#047857' },
};

export const ITEMS_PER_PAGE = 50;

export const CUSTOM_FILTER_OPERATORS = [
  { value: 'contains', label: 'Contém' },
  { value: 'notContains', label: 'Não contém' },
  { value: 'startsWith', label: 'Começa com' },
  { value: 'endsWith', label: 'Termina com' },
  { value: 'equals', label: 'É igual a' },
  { value: 'notEquals', label: 'É diferente de' },
  { value: 'gt', label: 'É maior que' },
  { value: 'lt', label: 'É menor que' },
  { value: 'gte', label: 'É igual ou maior a' },
  { value: 'lte', label: 'É igual ou menor a' },
  { value: 'empty', label: 'Está vazio' },
  { value: 'notEmpty', label: 'Não está vazio' },
] as const;

export type CustomFilterOperatorValue =
  (typeof CUSTOM_FILTER_OPERATORS)[number]['value'];

const labelMapFromOptions = (
  options: { value: number; label: string }[],
  value: number | null | undefined,
  fallback = '—',
): string => {
  if (value === null || value === undefined) return fallback;
  return options.find((o) => o.value === value)?.label ?? fallback;
};

export const getAccountLabel = (type: number | null | undefined): string => {
  if (type === null || type === undefined) return '—';
  return FINANCIAL_ENTRY_TYPE_TABLE_LABELS[type] ?? '—';
};

export const getTypeFormLabel = (type: number | null | undefined): string =>
  labelMapFromOptions(FINANCIAL_ENTRY_TYPE_OPTIONS, type ?? null);

export const getStatusLabel = (status: number | null | undefined): string =>
  labelMapFromOptions(FINANCIAL_ENTRY_STATUS_OPTIONS, status ?? null);

export const getCategoryLabel = (category: number | null | undefined): string =>
  labelMapFromOptions(FINANCIAL_ENTRY_CATEGORY_OPTIONS, category ?? null);

export const getSubcategoryLabel = (
  subcategory: number | null | undefined,
): string =>
  labelMapFromOptions(FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS, subcategory ?? null);

export const getRecurrenceLabel = (recurrence: number | null | undefined): string =>
  labelMapFromOptions(FINANCIAL_ENTRY_RECURRENCE_OPTIONS, recurrence ?? null);

export const getPaymentMethodLabel = (
  method: PaymentMethodLabelInput,
): string => resolvePaymentMethodLabel(method);

export const getSortOptionsForView = (
  viewBy: string | undefined,
): ReadonlyArray<{ value: FinancialEntrySortByValue; label: string }> => {
  switch (viewBy) {
    case FINANCIAL_VIEW_BY.PAYMENT:
      return FINANCIAL_ENTRY_SORT_BY_PAYMENT_OPTIONS;
    case FINANCIAL_VIEW_BY.ITEM:
      return FINANCIAL_ENTRY_SORT_BY_ITEM_OPTIONS;
    default:
      return FINANCIAL_ENTRY_SORT_BY_ENTRY_OPTIONS;
  }
};

export const getDefaultSortForView = (
  _viewBy?: string,
): FinancialEntrySortByValue => FINANCIAL_ENTRY_SORT_BY.CREATED_AT;

export const getSortByLabel = (
  sortBy: string | undefined,
  viewBy?: string,
): string => {
  const options = getSortOptionsForView(viewBy);
  if (!sortBy) return options[0]?.label ?? 'Registro';
  return options.find((o) => o.value === sortBy)?.label ?? options[0]?.label ?? 'Registro';
};

export const getViewByLabel = (viewBy: string | undefined): string => {
  if (!viewBy) return 'Lançamentos';
  return FINANCIAL_VIEW_BY_OPTIONS.find((o) => o.value === viewBy)?.label ?? 'Lançamentos';
};

export const getLabel = (
  group: 'type' | 'status' | 'category' | 'recurrence' | 'paymentMethod',
  value: number | null | undefined,
  paymentMethodName?: string | null,
): string => {
  switch (group) {
    case 'type':
      return getTypeFormLabel(value);
    case 'status':
      return getStatusLabel(value);
    case 'category':
      return getCategoryLabel(value);
    case 'recurrence':
      return getRecurrenceLabel(value);
    case 'paymentMethod':
      if (paymentMethodName) return getPaymentMethodLabel(paymentMethodName);
      if (value != null) return getPaymentMethodLabel({ id: value });
      return '—';
    default:
      return '—';
  }
};
